import React, { useEffect, useMemo, useRef, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { setDiagnostics } from "@codemirror/lint";
import type { EditorView } from "@codemirror/view";
import { useThemeStore } from "components/main/stores/themeStore";
import {
  buildExtensions,
  diagnosticToCM6,
  type CodeEditorDiagnostic,
  type CodeEditorKeyBinding,
  type CodeEditorLanguage,
} from "./extensions";
import "./codeEditorTheme.css";

export type { CodeEditorDiagnostic, CodeEditorKeyBinding, CodeEditorLanguage };

export interface CodeEditorProps {
  value: string;
  onChange: (next: string) => void;
  language: CodeEditorLanguage;
  /** Fixed pixel height, "auto" for content-driven height (requires minHeight +
   *  maxHeight), or "fill" for the full height of a parent that has one. */
  height?: number | "auto" | "fill";
  minHeight?: number;
  maxHeight?: number;
  lineNumbers?: boolean;
  /** Off scrolls long lines horizontally instead of wrapping them. */
  lineWrapping?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  /** "json" pretty-prints valid JSON on paste; invalid paste falls back to default. */
  formatOnPaste?: "json";
  /**
   * 1-indexed line/column markers (Postgres convention). Empty array clears
   * diagnostics. Callers should memoize this array — a fresh reference each
   * render dispatches a setDiagnostics transaction per render.
   */
  diagnostics?: CodeEditorDiagnostic[];
  keymap?: CodeEditorKeyBinding[];
  onBlur?: (value: string) => void;
  onMount?: (view: EditorView) => void;
  autoFocus?: boolean;
  /** Where to place the cursor on mount. Offsets are 0-indexed absolute. */
  initialCursor?: "end" | "between-brackets" | number;
  /** When true, includes the autocompletion extension (popup on typing). */
  enableAutocomplete?: boolean;
  /** Draw `{{name}}` as a chip. Read-only editors only. */
  slotTokens?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Shared CodeMirror 6 surface for inline code editing. Composes language,
 * theme, keymap, diagnostics, and (optionally) content-driven height.
 */
export const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  language,
  height = "auto",
  minHeight = 120,
  maxHeight = 400,
  lineNumbers = false,
  lineWrapping = true,
  readOnly = false,
  placeholder,
  formatOnPaste,
  diagnostics,
  keymap,
  onBlur,
  onMount,
  autoFocus,
  initialCursor,
  enableAutocomplete = false,
  slotTokens = false,
  className,
  style,
}) => {
  const isDark = useThemeStore((s) => s.theme === "dark");
  // The view is captured synchronously by @uiw's useLayoutEffect via the
  // onCreateEditor callback. We mirror it into useState so the diagnostic
  // useEffect can depend on it — refs don't trigger re-renders, and the
  // initial render finishes before the view is ready.
  const [view, setView] = useState<EditorView | null>(null);
  const onMountRef = useRef(onMount);
  onMountRef.current = onMount;

  // Stable identity for extensions: rebuilds only when a prop that actually
  // affects extension composition changes. uiw/react-codemirror diffs the
  // array by reference, so the memo matters.
  const extensions = useMemo(
    () =>
      buildExtensions({
        language,
        lineNumbers,
        readOnly,
        placeholder,
        formatOnPaste,
        keymap,
        onBlur,
        isDark,
        enableAutocomplete,
        lineWrapping,
        slotTokens,
      }),
    [
      language,
      lineNumbers,
      readOnly,
      placeholder,
      formatOnPaste,
      keymap,
      onBlur,
      isDark,
      enableAutocomplete,
      lineWrapping,
      slotTokens,
    ],
  );

  // Imperative diagnostic updates: setDiagnostics is the canonical way to push
  // markers without re-creating the editor. Depends on `view` so it re-runs
  // once @uiw finishes mounting and the view becomes available. The
  // `destroyed` check covers the race where `diagnostics` changes between
  // setView() firing and the component unmounting in the same tick.
  useEffect(() => {
    // view.dom.isConnected covers the race where `diagnostics` changes
    // between setView() firing and the component unmounting in the same
    // tick — dispatching against a destroyed view throws and wipes prior
    // state. (view.destroyed is private API; the DOM check is equivalent
    // and publicly observable.)
    if (!view || !view.dom.isConnected) return;
    const next = (diagnostics ?? []).map((d) => diagnosticToCM6(view, d));
    view.dispatch(setDiagnostics(view.state, next));
  }, [diagnostics, view]);

  const handleCreate = (created: EditorView) => {
    // Initial cursor placement happens once on mount.
    if (initialCursor !== undefined) {
      const doc = created.state.doc;
      let anchor: number;
      switch (initialCursor) {
        case "end":
          anchor = doc.length;
          break;
        case "between-brackets":
          // For `{}` or `[]`, place the caret between the brace pair.
          anchor = doc.length >= 2 ? 1 : doc.length;
          break;
        default:
          anchor = Math.max(0, Math.min(doc.length, initialCursor));
      }
      created.dispatch({ selection: { anchor } });
    }
    if (autoFocus) created.focus();
    setView(created);
    onMountRef.current?.(created);
  };

  // Height policy:
  //   - fixed (number)  → height: "Npx", lets @uiw size the editor and the
  //                       scroller fills 100% (built-in maxHeight scrolls).
  //   - auto            → minHeight + maxHeight strings; @uiw drives the
  //                       cap on .cm-editor and CM6's .cm-scroller scrolls
  //                       vertically when content exceeds maxHeight.
  //   - fill            → height:100% on both wrappers and the editor.
  //
  // Only `fill` wraps in height:100% — anywhere else it fights @uiw's own
  // theme rules and breaks scroll inside parents with no explicit height (the
  // magicbar JSON editor is exactly this case).
  const fill = height === "fill";
  const fixedHeight =
    typeof height === "number" ? `${height}px` : fill ? "100%" : undefined;
  const autoMinHeight = height === "auto" ? `${minHeight}px` : undefined;
  const autoMaxHeight = height === "auto" ? `${maxHeight}px` : undefined;

  return (
    <div
      className={fill ? `h-full ${className ?? ""}` : className}
      style={style}
    >
      <CodeMirror
        className={fill ? "h-full" : undefined}
        value={value}
        onChange={onChange}
        extensions={extensions}
        basicSetup={false}
        theme="none"
        editable={!readOnly}
        indentWithTab={false}
        height={fixedHeight}
        minHeight={autoMinHeight}
        maxHeight={autoMaxHeight}
        onCreateEditor={handleCreate}
      />
    </div>
  );
};
